import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageCircle, Image as ImageIcon, X, User, ArrowLeft, Reply, Edit2, Check } from 'lucide-react';
import { 
  sendDirectMessage, 
  subscribeToDirectMessages, 
  deleteDirectMessage, 
  updateDirectMessage,
  getBatchStudents,
  getInstituteTeachers,
  markMessageAsRead
} from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const BatchDirectMessages = ({ batchId }) => {
  const { currentUser, userProfile } = useRealtime();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const previousMessagesRef = useRef(new Map()); // Track previous messages to detect new ones

  // Keep ref in sync with state
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  const isTeacher = userProfile?.role === 'teacher' || userProfile?.role === 'admin';

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          console.log('Notification permission granted');
        }
      });
    }
  }, []);

  // Load available users (teachers for students, students for teachers)
  useEffect(() => {
    if (!batchId || !currentUser) return;

    const loadUsers = async () => {
      try {
        if (isTeacher) {
          // Teachers see list of students from their batch
          const result = await getBatchStudents(batchId);
          if (result.success) {
            setAvailableUsers(result.data || []);
          }
        } else {
          // Students see ALL teachers from the institute (coaching)
          const result = await getInstituteTeachers(batchId);
          if (result.success) {
            setAvailableUsers(result.data || []);
          }
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    loadUsers();
  }, [batchId, currentUser, isTeacher]);

  // Subscribe to conversations
  useEffect(() => {
    if (!batchId || !currentUser) return;

    setLoading(true);
    const unsubscribe = subscribeToDirectMessages(batchId, currentUser.uid, (result) => {
      setLoading(false);
      if (result.success) {
        // Group messages by conversation partner
        const conversationsMap = new Map();
        const newMessages = []; // Track new messages for notifications
        
        (result.data || []).forEach(msg => {
          const partnerId = msg.senderId === currentUser.uid ? msg.receiverId : msg.senderId;
          const partnerName = msg.senderId === currentUser.uid ? msg.receiverName : msg.senderName;
          const partnerEmail = msg.senderId === currentUser.uid ? msg.receiverEmail : msg.senderEmail;
          
          if (!conversationsMap.has(partnerId)) {
            conversationsMap.set(partnerId, {
              partnerId,
              partnerName,
              partnerEmail,
              lastMessage: msg,
              unreadCount: 0,
              messages: []
            });
          }
          
          const conversation = conversationsMap.get(partnerId);
          conversation.messages.push(msg);
          
          // Update last message if this one is newer
          if (!conversation.lastMessage || 
              (msg.createdAt?.getTime?.() || 0) > (conversation.lastMessage.createdAt?.getTime?.() || 0)) {
            conversation.lastMessage = msg;
          }
          
          // Count unread messages (messages received by current user that aren't read)
          if (msg.receiverId === currentUser.uid && !msg.read) {
            conversation.unreadCount++;
          }
          
          // Detect new messages (received by current user and not seen before)
          if (msg.receiverId === currentUser.uid && !previousMessagesRef.current.has(msg.id)) {
            newMessages.push({
              ...msg,
              partnerName
            });
          }
        });
        
        // Update previous messages map
        const currentMessagesMap = new Map();
        result.data.forEach(msg => {
          currentMessagesMap.set(msg.id, true);
        });
        
        // Show notifications for new messages (only if not from current user and not in selected conversation)
        newMessages.forEach(msg => {
          const isInSelectedConversation = selectedConversationRef.current?.partnerId === msg.senderId;
          
          // Only show notification if:
          // 1. Message is from someone else (not current user)
          // 2. Not currently viewing that conversation
          if (msg.senderId !== currentUser.uid && !isInSelectedConversation) {
            // Show toast notification
            toast.success(
              <div>
                <div className="font-semibold">{msg.partnerName}</div>
                <div className="text-sm">{msg.messageType === 'image' ? '📷 Sent an image' : msg.message}</div>
              </div>,
              {
                duration: 4000,
                icon: '💬',
              }
            );
            
            // Show browser notification if permission granted
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification(`New message from ${msg.partnerName}`, {
                body: msg.messageType === 'image' ? '📷 Sent an image' : msg.message,
                icon: '/logo192.png',
                tag: `message-${msg.id}`,
              });
            }
          }
        });
        
        // Update previous messages ref
        previousMessagesRef.current = currentMessagesMap;
        
        // Convert to array and sort by last message time
        const conversationsList = Array.from(conversationsMap.values()).sort((a, b) => {
          const aTime = a.lastMessage?.createdAt?.getTime?.() || 0;
          const bTime = b.lastMessage?.createdAt?.getTime?.() || 0;
          return bTime - aTime;
        });
        
        setConversations(conversationsList);
        
        // Update messages if a conversation is selected
        const currentSelectedPartnerId = selectedConversationRef.current?.partnerId;
        if (currentSelectedPartnerId) {
          const updatedConversation = conversationsList.find(c => c.partnerId === currentSelectedPartnerId);
          if (updatedConversation) {
            const sortedMessages = updatedConversation.messages.sort((a, b) => {
              const aTime = a.createdAt?.getTime?.() || 0;
              const bTime = b.createdAt?.getTime?.() || 0;
              return aTime - bTime;
            });
            setMessages(sortedMessages);
            
            // Mark messages as read when viewing the conversation
            updatedConversation.messages.forEach(msg => {
              if (msg.receiverId === currentUser.uid && !msg.read) {
                markMessageAsRead(msg.id).catch(err => alert('Failed to mark as read: ' + err));
              }
            });
          }
        }
      } else {
        // Show error on screen
        alert('Failed to load conversations: ' + (result.error || 'Unknown error'));
        toast.error('Failed to load conversations: ' + (result.error || 'Unknown error'));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [batchId, currentUser]); // Removed selectedConversation from dependencies

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setMessages(conversation.messages.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return aTime - bTime;
    }));
    setEditingMessage(null);
    setReplyingTo(null);
  };

  const handleStartNewChat = (user) => {
    const existingConversation = conversations.find(c => c.partnerId === user.uid || c.partnerId === user.id);
    
    if (existingConversation) {
      handleSelectConversation(existingConversation);
    } else {
      setSelectedConversation({
        partnerId: user.uid || user.id,
        partnerName: user.name || user.displayName || user.email,
        partnerEmail: user.email,
        messages: [],
        unreadCount: 0
      });
      setMessages([]);
    }
    
    setShowNewChatModal(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !selectedConversation || sending) return;

    // If editing, update the message
    if (editingMessage) {
      try {
        setSending(true);
        const result = await updateDirectMessage(editingMessage.id, {
          message: newMessage.trim()
        });

        if (result.success) {
          setNewMessage('');
          setEditingMessage(null);
          toast.success('Message updated');
        } else {
          console.error('Failed to update message:', result.error);
          toast.error('Failed to update message: ' + (result.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error updating message:', error);
        toast.error('Error updating message: ' + error.message);
      } finally {
        setSending(false);
      }
      return;
    }

    // Send new message
    try {
      setSending(true);
      
      console.log('=== SENDING DIRECT MESSAGE ===');
      console.log('Batch ID:', batchId);
      console.log('Sender ID:', currentUser.uid);
      console.log('Sender Name:', userProfile?.displayName || userProfile?.name || currentUser.displayName);
      console.log('Receiver ID:', selectedConversation.partnerId);
      console.log('Receiver Name:', selectedConversation.partnerName);
      console.log('Message:', newMessage.trim());
      
      const result = await sendDirectMessage({
        batchId,
        senderId: currentUser.uid,
        senderName: userProfile?.displayName || userProfile?.name || currentUser.displayName || 'Anonymous',
        senderEmail: currentUser.email,
        receiverId: selectedConversation.partnerId,
        receiverName: selectedConversation.partnerName,
        receiverEmail: selectedConversation.partnerEmail,
        message: newMessage.trim(),
        messageType: 'text',
        replyTo: replyingTo?.id || null,
        replyToMessage: replyingTo?.message || null,
        replyToSender: replyingTo?.senderName || null
      });

      console.log('=== SEND RESULT ===');
      console.log('Success:', result.success);
      console.log('Message ID:', result.id);
      if (!result.success) {
        console.error('Error:', result.error);
      }

      if (result.success) {
        setNewMessage('');
        setReplyingTo(null);
        toast.success('Message sent');
      } else {
        console.error('Failed to send message:', result.error);
        toast.error('Failed to send message: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error sending message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file || !currentUser || !selectedConversation) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const fileName = `direct-messages/${batchId}/${Date.now()}_${file.name}`;
      const uploadResult = await uploadFile(file, fileName);

      if (uploadResult.success) {
        const result = await sendDirectMessage({
          batchId,
          senderId: currentUser.uid,
          senderName: userProfile?.displayName || userProfile?.name || currentUser.displayName || 'Anonymous',
          senderEmail: currentUser.email,
          receiverId: selectedConversation.partnerId,
          receiverName: selectedConversation.partnerName,
          receiverEmail: selectedConversation.partnerEmail,
          message: '',
          messageType: 'image',
          fileUrl: uploadResult.url,
          fileName: file.name
        });

        if (!result.success) {
          toast.error('Failed to send image');
        }
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      toast.error('Error uploading file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      const result = await deleteDirectMessage(messageId);
      if (result.success) {
        toast.success('Message deleted');
      } else {
        toast.error('Failed to delete message');
      }
    } catch (error) {
      toast.error('Error deleting message');
    }
  };

  const handleEditMessage = (message) => {
    if (message.messageType !== 'text') {
      toast.error('Only text messages can be edited');
      return;
    }
    setEditingMessage(message);
    setNewMessage(message.message || '');
    setReplyingTo(null);
  };

  const handleReplyToMessage = (message) => {
    setReplyingTo({
      id: message.id,
      senderName: message.senderName,
      message: message.message || (message.messageType === 'image' ? 'Image' : 'Message')
    });
    setEditingMessage(null);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setEditingMessage(null);
    setNewMessage('');
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate?.() || new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const isOwnMessage = (message) => {
    return message.senderId === currentUser?.uid;
  };

  return (
    <div className="flex h-full bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      {/* Conversations List */}
      <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-gray-200 dark:border-gray-700`}>
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Messages</h2>
            <button
              onClick={() => setShowNewChatModal(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              New Chat
            </button>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {isTeacher ? 'Chat with your students' : 'Chat with your teachers'}
          </p>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <MessageCircle className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 dark:text-gray-400 text-sm">No conversations yet</p>
              <button
                onClick={() => setShowNewChatModal(true)}
                className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Start a conversation
              </button>
            </div>
          ) : (
            conversations.map((conversation) => (
              <button
                key={conversation.partnerId}
                onClick={() => handleSelectConversation(conversation)}
                className={`w-full px-4 py-3 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left ${
                  selectedConversation?.partnerId === conversation.partnerId ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {conversation.partnerName}
                      </h3>
                      {conversation.unreadCount > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                          {conversation.unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {conversation.lastMessage?.messageType === 'image' ? '📷 Image' : conversation.lastMessage?.message || 'No messages yet'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {formatTime(conversation.lastMessage?.createdAt)}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="md:hidden text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {selectedConversation.partnerName}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {selectedConversation.partnerEmail}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-600 dark:text-gray-400">No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex group ${isOwnMessage(message) ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-lg ${
                        isOwnMessage(message)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}
                    >
                      {message.replyTo && message.replyToMessage && (
                        <div className={`mb-1 pb-1 border-l-2 pl-2 ${
                          isOwnMessage(message) ? 'border-blue-300 opacity-80' : 'border-gray-400'
                        }`}>
                          <div className="text-xs font-medium opacity-75">
                            {message.replyToSender}
                          </div>
                          <div className="text-xs opacity-70 truncate">
                            {message.replyToMessage}
                          </div>
                        </div>
                      )}
                      
                      {message.messageType === 'image' && message.fileUrl ? (
                        <div>
                          <img
                            src={message.fileUrl}
                            alt={message.fileName || 'Image'}
                            className="max-w-full h-auto rounded mb-1 cursor-pointer"
                            onClick={() => window.open(message.fileUrl, '_blank')}
                          />
                          {message.message && (
                            <p className="mt-1 text-sm">{message.message}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap break-words">{message.message}</p>
                      )}
                      
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className={`text-xs ${isOwnMessage(message) ? 'text-blue-100' : 'text-gray-500'}`}>
                          {formatTime(message.createdAt)}
                        </span>
                        {isOwnMessage(message) && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEditMessage(message)}
                              className="p-1 hover:bg-white/20 rounded"
                              title="Edit"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(message.id)}
                              className="p-1 hover:bg-white/20 rounded"
                              title="Delete"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {!isOwnMessage(message) && (
                          <button
                            onClick={() => handleReplyToMessage(message)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                            title="Reply"
                          >
                            <Reply className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              {replyingTo && (
                <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-blue-700 dark:text-blue-300">
                        Replying to {replyingTo.senderName}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {replyingTo.message}
                      </div>
                    </div>
                    <button onClick={handleCancelReply} className="text-gray-500 hover:text-gray-700 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {editingMessage && (
                <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Edit2 className="w-4 h-4 text-yellow-700 dark:text-yellow-300" />
                      <span className="text-xs font-medium text-yellow-700 dark:text-yellow-300">
                        Editing message
                      </span>
                    </div>
                    <button onClick={handleCancelReply} className="text-gray-500 hover:text-gray-700 p-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleFileUpload(e.target.files[0]);
                    }
                  }}
                />
                {!editingMessage && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
                  >
                    {uploading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    ) : (
                      <ImageIcon className="w-5 h-5" />
                    )}
                  </button>
                )}
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={editingMessage ? "Edit message..." : "Type a message..."}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending || uploading}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : editingMessage ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isTeacher ? 'Select Student' : 'Select Teacher'}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {isTeacher ? 'Choose a student to message' : 'All teachers from your institute'}
                </p>
              </div>
              <button
                onClick={() => setShowNewChatModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {availableUsers.length === 0 ? (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8">
                  No {isTeacher ? 'students' : 'teachers'} found
                </p>
              ) : (
                <div className="space-y-2">
                  {availableUsers.map((user) => (
                    <button
                      key={user.uid || user.id}
                      onClick={() => handleStartNewChat(user)}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900 dark:text-white truncate">
                              {user.name || user.displayName || 'Unknown'}
                            </h4>
                            {!isTeacher && user.role && (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                user.role === 'admin' 
                                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              }`}>
                                {user.role === 'admin' ? 'Admin' : 'Teacher'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {user.email}
                          </p>
                          {!isTeacher && user.subject && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                              {user.subject}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchDirectMessages;
